import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Search, Pencil, Trash2, X, UserCircle2, Crown, User as UserIcon, Mail, Shield, UserPlus, ListTodo, Send } from "lucide-react";
import Confirm from "@/components/Confirm";
import InviteUserModal from "@/components/InviteUserModal";
import { toast } from "@/components/Toaster";

const SEED_ADMIN_EMAIL = "admin@sovereignhpc.com.au";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users
      .filter((u) => {
        if (roleFilter === "all") return true;
        if (roleFilter === "owner") return !!u.owner;
        return u.role === roleFilter;
      })
      .filter((u) => {
        if (!needle) return true;
        return (u.email || "").toLowerCase().includes(needle)
          || (u.name || "").toLowerCase().includes(needle);
      })
      .sort((a, b) => (a.email || "").localeCompare(b.email || ""));
  }, [users, q, roleFilter]);

  const summary = useMemo(() => {
    const owners = users.filter((u) => u.owner).length;
    const admins = users.filter((u) => u.role === "admin").length;
    const pending = users.filter((u) => u.pending_invite).length;
    const mfa = users.filter((u) => u.mfa_enabled).length;
    return { total: users.length, owners, admins, pending, mfa };
  }, [users]);

  const askDelete = (u) => {
    setConfirmState({
      title: `Delete ${u.name || u.email}?`,
      message: `This permanently removes the account, all room memberships, permissions, invites, notifications, password resets and login history for "${u.email}". The user will be unable to sign in. This cannot be undone.`,
      confirmLabel: "Delete user",
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/users/${u.id}`);
          toast.success(`Deleted ${u.email}`);
          await load();
        } catch (e) {
          toast.error(formatError(e));
          throw e;
        }
      },
    });
  };

  // App Owner / Admin redundancy lever: re-send a fresh access link to an
  // existing user who can't sign in. Confirms first because firing this
  // also invalidates any in-flight password-reset link the user might be
  // about to click — we don't want the Owner to accidentally orphan a
  // user mid-recovery.
  const askResend = (u) => {
    setConfirmState({
      title: `Resend access link to ${u.email}?`,
      message: `${u.name ? `${u.name} ` : ""}will receive a new one-time sign-in link by email (valid for 24 hours). Any older invite or reset link for this account will be invalidated. Use this when the user cannot sign in or never received the original invite.`,
      confirmLabel: "Resend access link",
      onConfirm: async () => {
        try {
          await api.post(`/users/${u.id}/resend-access`);
          toast.success(`Access link sent to ${u.email}`);
        } catch (e) {
          toast.error(formatError(e));
          throw e;
        }
      },
    });
  };

  const isSelf = (u) => u.id === me?.id;
  const isSeed = (u) => (u.email || "").toLowerCase() === SEED_ADMIN_EMAIL;
  // Only App Owners can edit roles or delete other accounts. Regular admins
  // can view the directory (and edit their own profile from /settings) but
  // never modify other users' privileges.
  const amOwner = !!me?.owner;
  const canEdit = (u) => amOwner && !isSelf(u) && !isSeed(u);
  const canDelete = (u) => amOwner && !isSelf(u) && !isSeed(u) && !u.owner;
  // Sending a fresh access-link email is Owner-only. Admins can help
  // users in distress via other channels (password self-reset, room
  // membership tools) — but only Owners can trigger a new invite email
  // from this table.  Never allow resending to your own account —
  // use /settings → password reset for that.
  const canResend = (u) => amOwner && !isSelf(u);

  return (
    <div className="p-8">
      <div className="label-mono text-muted-foreground">— Admin / Users</div>
      <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-display text-4xl tracking-tighter font-medium">User directory</h1>
        {amOwner && (
          <button
            data-testid="users-invite-btn"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:opacity-90 transition"
            title="Invite a new user by email to one or more data rooms"
          >
            <UserPlus className="w-4 h-4" /> Invite user
          </button>
        )}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Manage every account that has ever touched a data room. The seed admin and your own account are protected from deletion.</p>

      {!amOwner && (
        <div className="mt-4 flex items-start gap-3 p-3 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06]">
          <Shield className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-foreground">Read-only directory</div>
            <p className="text-muted-foreground mt-0.5">
              Editing roles and deleting accounts is restricted to <span className="text-emerald-500 font-medium">App Owners</span>. Reach out to an Owner if you need a change here.
            </p>
          </div>
        </div>
      )}

      {/* Summary tiles */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total users" value={summary.total} icon={UserCircle2} />
        <StatTile label="App Owners" value={summary.owners} icon={Shield} accent="text-emerald-500" />
        <StatTile label="Admins" value={summary.admins} icon={Crown} accent="text-amber-500" />
        <StatTile label="Pending invites" value={summary.pending} icon={Mail} accent="text-sky-500" />
        <StatTile label="MFA enabled" value={summary.mfa} icon={ShieldCheck} accent="text-emerald-500" />
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            data-testid="users-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full pl-10 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex border border-border rounded-md overflow-hidden">
          {["all", "owner", "admin", "user"].map((r) => (
            <button
              key={r}
              onClick={() => setRoleFilter(r)}
              className={`px-3 py-2 text-xs uppercase tracking-wider transition ${
                roleFilter === r ? "bg-primary text-primary-foreground" : "hover:bg-secondary"
              }`}
              data-testid={`users-filter-${r}`}
            >{r}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-4 border border-border rounded-md overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr_110px_90px_110px_180px] label-mono text-muted-foreground border-b border-border bg-secondary/60">
          <div className="px-4 py-3">Email</div>
          <div className="px-4 py-3 hidden md:block">Name</div>
          <div className="px-4 py-3">Role</div>
          <div className="px-4 py-3">MFA</div>
          <div className="px-4 py-3">Status</div>
          <div className="px-4 py-3 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-sm text-muted-foreground text-center">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-sm text-muted-foreground text-center">
            No users match your search.
          </div>
        ) : filtered.map((u) => (
          <div key={u.id} data-testid={`user-row-${u.id}`} className="grid grid-cols-[1fr_1fr_110px_90px_110px_180px] items-center border-b border-border last:border-b-0 hover:bg-secondary/40 transition">
            <div className="px-4 py-3 text-sm flex items-center gap-2 min-w-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                u.owner ? "bg-emerald-500/15 text-emerald-500"
                  : u.role === "admin" ? "bg-amber-500/15 text-amber-600"
                  : "bg-secondary text-muted-foreground"
              }`}>
                {u.owner ? <Shield className="w-3.5 h-3.5" />
                  : u.role === "admin" ? <Crown className="w-3.5 h-3.5" />
                  : <UserIcon className="w-3.5 h-3.5" />}
              </div>
              <div className="min-w-0">
                <div className="truncate font-medium">{u.email}</div>
                <div className="md:hidden truncate text-xs text-muted-foreground">{u.name}</div>
              </div>
            </div>
            <div className="px-4 py-3 text-sm text-muted-foreground hidden md:block truncate">{u.name || "—"}</div>
            <div className="px-4 py-3 mono text-xs uppercase tracking-wider">
              {u.owner ? (
                <span
                  data-testid={`role-pill-owner-${u.id}`}
                  className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-500"
                  title="App Owner — top-tier privileges"
                >Owner</span>
              ) : u.role === "admin" ? (
                <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-600">Admin</span>
              ) : (
                <span className="px-2 py-0.5 rounded bg-secondary text-muted-foreground">User</span>
              )}
            </div>
            <div className="px-4 py-3 mono text-xs">
              {u.mfa_enabled
                ? <span className="inline-flex items-center gap-1 text-emerald-500"><ShieldCheck className="w-3 h-3" /> On</span>
                : <span className="text-muted-foreground">Off</span>}
            </div>
            <div className="px-4 py-3 text-xs">
              {u.pending_invite
                ? <span className="inline-flex items-center gap-1.5 mono uppercase tracking-wider text-sky-500"><span className="w-1.5 h-1.5 rounded-full bg-sky-500" /> Pending</span>
                : <span className="inline-flex items-center gap-1.5 mono uppercase tracking-wider text-emerald-500"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active</span>}
            </div>
            <div className="px-4 py-3 flex justify-end gap-1">
              {amOwner && (
                <button
                  data-testid={`user-resend-${u.id}`}
                  onClick={() => canResend(u) && askResend(u)}
                  disabled={!canResend(u)}
                  title={
                    canResend(u)
                      ? `Email a fresh sign-in link to ${u.email} (use when the user can't log in)`
                      : isSelf(u)
                        ? "Use /settings → password reset for your own account"
                        : "Resending is Owner-only"
                  }
                  className="p-2 rounded-md text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                data-testid={`user-edit-${u.id}`}
                onClick={() => canEdit(u) && setEditing(u)}
                disabled={!canEdit(u)}
                title={canEdit(u) ? "Edit user" : isSelf(u) ? "You cannot edit your own account here" : "The seed admin is protected"}
                className="p-2 rounded-md text-indigo-500 hover:bg-indigo-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`user-delete-${u.id}`}
                onClick={() => canDelete(u) && askDelete(u)}
                disabled={!canDelete(u)}
                title={canDelete(u) ? "Delete user" : isSelf(u) ? "You cannot delete yourself" : "The seed admin is protected"}
                className="p-2 rounded-md text-rose-600 hover:bg-destructive hover:text-destructive-foreground disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditUserDialog
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
      <Confirm state={confirmState} onClose={() => setConfirmState(null)} />
      {inviteOpen && (
        <InviteUserModal
          onClose={() => setInviteOpen(false)}
          onSent={async () => { setInviteOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, accent }) {
  return (
    <div className="border border-border bg-card rounded-md p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-md bg-secondary flex items-center justify-center ${accent || "text-muted-foreground"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="font-display text-2xl tracking-tight">{value}</div>
        <div className="label-mono text-muted-foreground">{label}</div>
      </div>
    </div>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [role, setRole] = useState(user.role || "user");
  const [ownerFlag, setOwnerFlag] = useState(!!user.owner);
  // Tasks-feature access flag. Owners + Admins always have access (the
  // backend ignores the toggle for privileged users), so we render the
  // control disabled in that case to communicate the immutable state.
  const [tasksAccess, setTasksAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Hydrate current `can_use_tasks` for this user from the owner roster.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/tasks/access");
        const row = (data || []).find((r) => r.id === user.id);
        if (alive && row) setTasksAccess(!!row.can_use_tasks);
      } catch { /* ignore — toggle just stays at default */ }
    })();
    return () => { alive = false; };
  }, [user.id]);

  const isPrivileged = ownerFlag || role === "admin";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Sequence the role/name update and the owner-flag toggle so each
      // backend guard can fire its own helpful error message.
      await api.patch(`/users/${user.id}`, { name: name.trim(), role });
      if (ownerFlag !== !!user.owner) {
        await api.patch(`/users/${user.id}/owner`, { owner: ownerFlag });
      }
      // Persist task access for non-privileged users only — privileged
      // users always have it, so we skip the call.
      if (!isPrivileged) {
        await api.post(`/tasks/access/${user.id}`, { can_use_tasks: tasksAccess });
      }
      toast.success(`Updated ${user.email}`);
      onSaved?.();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-md w-full max-w-md p-6 relative"
        data-testid="user-edit-dialog"
      >
        <button type="button" onClick={onClose} aria-label="Close" className="absolute top-3 right-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition">
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-display text-xl tracking-tight">Edit user</h3>
        <p className="text-xs text-muted-foreground mt-1 truncate">{user.email}</p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="label-mono">Name</label>
            <input
              data-testid="user-edit-name"
              required value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="label-mono">Role</label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["user", "admin"].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  disabled={ownerFlag && r === "user"}
                  data-testid={`user-edit-role-${r}`}
                  className={`px-3 py-2 rounded-md text-sm border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                    role === r ? "border-primary bg-primary/10 text-foreground" : "border-border hover:bg-secondary"
                  }`}
                >
                  {r === "admin"
                    ? <span className="inline-flex items-center gap-2"><Crown className="w-3.5 h-3.5 text-amber-500" /> Admin</span>
                    : <span className="inline-flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> User</span>}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">Admins can manage rooms, members, permissions and content. App Owners can additionally change roles and approve access requests.</p>
          </div>
          <div>
            <label className="label-mono flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5"><Shield className="w-3 h-3 text-emerald-500" /> App Owner</span>
              <button
                type="button"
                onClick={() => {
                  const next = !ownerFlag;
                  setOwnerFlag(next);
                  // Owners are always admins — auto-lift the role.
                  if (next) setRole("admin");
                }}
                data-testid="user-edit-owner-toggle"
                role="switch" aria-checked={ownerFlag}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                  ownerFlag ? "bg-emerald-500" : "bg-secondary"
                }`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background shadow transition ${
                  ownerFlag ? "translate-x-5" : "translate-x-1"
                }`} />
              </button>
            </label>
            <p className="text-xs text-muted-foreground mt-2">
              {ownerFlag
                ? "Owner — full role-management privileges and access-request approval."
                : "Promote to grant role-management privileges and access-request approval."}
            </p>
          </div>
          <div>
            <label className="label-mono flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5"><ListTodo className="w-3 h-3 text-primary" /> Tasks access</span>
              <button
                type="button"
                disabled={isPrivileged}
                onClick={() => setTasksAccess((v) => !v)}
                data-testid="user-edit-tasks-toggle"
                role="switch" aria-checked={isPrivileged || tasksAccess}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-70 ${
                  (isPrivileged || tasksAccess) ? "bg-primary" : "bg-secondary"
                }`}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background shadow transition ${
                  (isPrivileged || tasksAccess) ? "translate-x-5" : "translate-x-1"
                }`} />
              </button>
            </label>
            <p className="text-xs text-muted-foreground mt-2">
              {isPrivileged
                ? "Owners and Admins always have access to Tasks."
                : tasksAccess
                  ? "Enabled — this user sees the Tasks workspace in the sidebar."
                  : "Off — grant access so this user can use the workspace To-Do list."}
            </p>
          </div>
        </div>

        {err && <div className="mt-4 text-sm text-destructive">{err}</div>}

        <div className="mt-6 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition">Cancel</button>
          <button
            data-testid="user-edit-save"
            type="submit" disabled={busy}
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
